import * as React from "react";

import { cn } from "@/lib/utils";

export interface PageTitleBarProps {
  title: string;
  /** Merges with {@link PAGE_TITLE_BAR_APP_TITLE_CLASS}. */
  titleClassName?: string;
  /** Semantic heading level for the title. Default: `h1`. */
  titleAs?: "h1" | "h2" | "span";
  /** Optional line under the title (Dashboard-style). */
  subtitle?: string;
  /** Renders before the title (e.g. back, menu). No extra margin. */
  left?: React.ReactNode;
  /** Inline after the title (same row), before `right`. */
  titleSuffix?: React.ReactNode;
  /** Renders after the title block (e.g. actions, filter). */
  right?: React.ReactNode;
  /** Optional full-width row below the main row; consumer controls inner padding. */
  below?: React.ReactNode;
  className?: string;
  /** Extra classes on the main title row (e.g. `h-full`, custom horizontal padding). */
  rowClassName?: string;
}

/** Default page title look: bold, responsive size, horizontal padding aligned with hub shells. */
export const PAGE_TITLE_BAR_APP_TITLE_CLASS =
  "truncate font-bold text-xl md:text-2xl pl-1 sm:pl-2 md:pl-4";

/**
 * Shared title row for in-page chrome (typically inside a ~60px-tall header strip).
 *
 * Does not apply top safe-area insets: `Layout` already handles that.
 * No routing, no local state, no `fixed`/`absolute` positioning.
 */
export function PageTitleBar({
  title,
  titleClassName,
  titleAs = "h1",
  subtitle,
  left,
  titleSuffix,
  right,
  below,
  className,
  rowClassName,
}: PageTitleBarProps) {
  const titleCn = cn(PAGE_TITLE_BAR_APP_TITLE_CLASS, titleClassName);
  const titleNode =
    titleAs === "span" ? (
      <span className={cn(titleCn, "block min-w-0")}>{title}</span>
    ) : titleAs === "h2" ? (
      <h2 className={titleCn}>{title}</h2>
    ) : (
      <h1 className={titleCn}>{title}</h1>
    );

  return (
    <div className={cn("flex w-full flex-col", className)}>
      <div
        className={cn(
          "flex min-h-[60px] items-center gap-2 px-3 sm:px-4",
          rowClassName,
        )}
      >
        {left}
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <div className="flex min-w-0 items-center gap-2">
            {titleNode}
            {titleSuffix ? (
              <span className="flex shrink-0 items-center">{titleSuffix}</span>
            ) : null}
          </div>
          {subtitle ? (
            <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
          ) : null}
        </div>
        {right ? (
          <div className="flex shrink-0 items-center gap-2">{right}</div>
        ) : null}
      </div>
      {below ? <div className="w-full">{below}</div> : null}
    </div>
  );
}
